import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Camera, Eye, EyeOff, ArrowRight, CheckCircle } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const PhotographerSignup = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const inviteCode = searchParams.get("code") || "";
  
  const [step, setStep] = useState(1);
  const [validating, setValidating] = useState(true);
  const [codeValid, setCodeValid] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    invite_code: inviteCode,
    name: "",
    email: "",
    username: "",
    password: "",
    confirmPassword: "",
    bio: "",
    website: "",
    instagram: ""
  });

  useEffect(() => {
    validateCode();
  }, [inviteCode]);

  const validateCode = async () => {
    if (!inviteCode) {
      setValidating(false);
      setError("No invite code provided. Please use a valid invite link.");
      return;
    }
    
    try {
      await axios.get(`${API}/photographers/invite/${inviteCode}/validate`);
      setCodeValid(true);
    } catch (err) {
      setError("This invite link is invalid or has expired.");
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    if (step === 1) {
      if (formData.password !== formData.confirmPassword) {
        setError("Passwords do not match");
        return;
      }
      if (formData.password.length < 8) {
        setError("Password must be at least 8 characters");
        return;
      }
      setStep(2);
      return;
    }
    
    // Submit signup
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/photographers/signup`, {
        invite_code: inviteCode,
        name: formData.name,
        email: formData.email,
        username: formData.username,
        password: formData.password,
        bio: formData.bio || null,
        website: formData.website || null,
        instagram: formData.instagram || null
      });
      
      localStorage.setItem("photographer_token", res.data.token);
      localStorage.setItem("photographer_user", JSON.stringify(res.data.photographer));
      navigate("/photographer/portal");
    } catch (err) {
      setError(err.response?.data?.detail || "Signup failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  if (!codeValid) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <Camera className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Invalid Invite</h1>
          <p className="text-zinc-500 mb-6">{error}</p>
          <Link to="/" className="text-orange-500 hover:text-orange-400">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4" data-testid="photographer-signup">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <Camera className="w-12 h-12 text-orange-500 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-white mb-2">Photographer Portal</h1>
          <p className="text-zinc-500">Create your account to upload photos</p>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${step >= 1 ? 'bg-orange-500 text-black' : 'bg-zinc-800 text-zinc-500'}`}>
            1
          </div>
          <div className={`w-16 h-0.5 ${step >= 2 ? 'bg-orange-500' : 'bg-zinc-800'}`} />
          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${step >= 2 ? 'bg-orange-500 text-black' : 'bg-zinc-800 text-zinc-500'}`}>
            2
          </div>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-500 px-4 py-3 mb-6 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-zinc-900/50 border border-white/5 p-6 space-y-4">
          {step === 1 && (
            <>
              <h2 className="text-white font-bold mb-4">Account Information</h2>
              
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Full Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 bg-black border border-white/10 text-white focus:border-orange-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-1">Email *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 bg-black border border-white/10 text-white focus:border-orange-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-1">Username *</label>
                <input
                  type="text"
                  required
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                  className="w-full px-4 py-3 bg-black border border-white/10 text-white focus:border-orange-500 focus:outline-none"
                  placeholder="your_username"
                />
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-1">Password *</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-4 py-3 bg-black border border-white/10 text-white focus:border-orange-500 focus:outline-none pr-12"
                    placeholder="Min 8 characters"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-1">Confirm Password *</label>
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className="w-full px-4 py-3 bg-black border border-white/10 text-white focus:border-orange-500 focus:outline-none"
                />
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <h2 className="text-white font-bold mb-4">Profile Information (Optional)</h2>
              
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Bio</label>
                <textarea
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  className="w-full px-4 py-3 bg-black border border-white/10 text-white focus:border-orange-500 focus:outline-none h-24 resize-none"
                  placeholder="Tell us about yourself..."
                />
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-1">Website</label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  className="w-full px-4 py-3 bg-black border border-white/10 text-white focus:border-orange-500 focus:outline-none"
                  placeholder="https://yourwebsite.com"
                />
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-1">Instagram Handle</label>
                <input
                  type="text"
                  value={formData.instagram}
                  onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                  className="w-full px-4 py-3 bg-black border border-white/10 text-white focus:border-orange-500 focus:outline-none"
                  placeholder="@yourusername"
                />
              </div>
            </>
          )}

          <div className="flex items-center gap-4 pt-4">
            {step === 2 && (
              <button
                type="button"
                onClick={() => setStep(1)}
                className="px-6 py-3 border border-white/20 text-white hover:bg-white/5"
              >
                Back
              </button>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-orange-500 text-black font-bold hover:bg-orange-400 disabled:opacity-50"
            >
              {submitting ? (
                <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
              ) : step === 1 ? (
                <>Next <ArrowRight className="w-4 h-4" /></>
              ) : (
                <>Create Account <CheckCircle className="w-4 h-4" /></>
              )}
            </button>
          </div>
        </form>

        <p className="text-center text-zinc-600 text-sm mt-6">
          Already have an account?{" "}
          <Link to="/photographer/login" className="text-orange-500 hover:text-orange-400">
            Sign In
          </Link>
        </p>
      </div>
    </div>
  );
};

export default PhotographerSignup;
